import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Client, Form, FormSchema, SuspensionResult } from '../types.js';
import { getClient, formsApi, suspendClient, resumeClient } from '../api.js';
import SchemaEditor from '../components/SchemaEditor.js';

const DEFAULT_SCHEMA: FormSchema = {
  fields: [],
  submit_button_text: 'Submit',
  success_message: "Thanks for your submission. We'll be in touch.",
};

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState('');

  const [suspension, setSuspension] = useState<SuspensionResult | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [suspensionError, setSuspensionError] = useState('');

  // Forms state
  const [forms, setForms] = useState<Form[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormSchema, setNewFormSchema] = useState<FormSchema>(DEFAULT_SCHEMA);
  const [creatingForm, setCreatingForm] = useState(false);
  const [createError, setCreateError] = useState('');

  function loadForms(clientId: string) {
    setFormsLoading(true);
    formsApi.list(clientId)
      .then((res) => setForms(res.forms))
      .catch((e) => setFormsError(e instanceof Error ? e.message : 'Failed to load forms'))
      .finally(() => setFormsLoading(false));
  }

  useEffect(() => {
    if (!id) return;
    getClient(id)
      .then((res) => setClient(res.client))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
    loadForms(id);
  }, [id]);

  async function handleCreateForm(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setCreatingForm(true);
    setCreateError('');
    try {
      await formsApi.create({
        client_id: id,
        name: newFormName.trim(),
        form_schema: newFormSchema,
        is_primary: forms.length === 0, // first form is primary
      });
      setShowNewForm(false);
      setNewFormName('');
      setNewFormSchema(DEFAULT_SCHEMA);
      loadForms(id);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create form');
    } finally {
      setCreatingForm(false);
    }
  }

  async function handleDeleteForm(formId: string, formName: string) {
    if (!confirm(`Delete form "${formName}"? This cannot be undone.`)) return;
    try {
      await formsApi.delete(formId);
      if (id) loadForms(id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete form');
    }
  }

  async function runSuspension(kind: 'suspend' | 'resume') {
    if (!client) return;
    if (kind === 'suspend' && !confirm(
      `Suspend ${client.name}? All their websites will show a "contact support" page and their forms will stop taking enquiries until you resume them.`,
    )) return;
    setSuspending(true);
    setSuspensionError('');
    try {
      const result = kind === 'suspend' ? await suspendClient(client.id) : await resumeClient(client.id);
      setSuspension(result);
      const fresh = await getClient(client.id);
      setClient(fresh.client);
    } catch (e) {
      setSuspensionError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setSuspending(false);
    }
  }

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!client) return <p style={{ color: '#94a3b8' }}>Loading…</p>;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">{client.name}</h1>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <Link to={`/clients/${client.id}/edit`} className="btn btn-secondary">Edit client</Link>
          <Link to={`/clients/${client.id}/submissions`} className="btn btn-primary">View submissions</Link>
          {client.suspended_at === undefined && (
            <button className="btn btn-danger" disabled={suspending} onClick={() => { void runSuspension('suspend'); }}>
              {suspending ? 'Suspending…' : 'Suspend (unpaid)'}
            </button>
          )}
        </div>
      </div>

      {client.suspended_at !== undefined && (
        <div className="alert alert-warning" style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span>
            <strong>Suspended since {new Date(client.suspended_at * 1000).toLocaleString('en-AU')}</strong> — websites show
            the contact page and forms are closed.
          </span>
          <button className="btn btn-primary" disabled={suspending} onClick={() => { void runSuspension('resume'); }}>
            {suspending ? 'Resuming…' : 'Resume'}
          </button>
        </div>
      )}

      {suspensionError && <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>{suspensionError}</div>}

      {suspension && suspension.sites.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <table>
            <thead><tr><th>Site</th><th>Result</th></tr></thead>
            <tbody>
              {suspension.sites.map((s) => (
                <tr key={s.site_id}>
                  <td><Link to={`/sites/${s.site_id}`}><code>{s.site_id}</code></Link></td>
                  <td>
                    {s.ok
                      ? <span className="badge badge-green">Done</span>
                      : <span className="badge badge-red">Failed</span>}
                    {(s.error ?? s.note) && <span style={{ marginLeft: '.5rem', fontSize: '.85rem' }}>{s.error ?? s.note}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {suspension.sites.some((s) => !s.ok) && (
            <div style={{ marginTop: '.75rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={suspending}
                onClick={() => { void runSuspension(suspension.suspended_at !== null ? 'suspend' : 'resume'); }}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <table>
          <tbody>
            <tr><th style={{ width: '180px' }}>Status</th><td>{client.status}</td></tr>
            <tr><th>Client ID</th><td><code>{client.id}</code></td></tr>
            <tr><th>Created</th><td>{new Date(client.created_at * 1000).toLocaleString('en-AU')}</td></tr>
          </tbody>
        </table>
      </div>

      {client.gtm_container_id && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Tracking</h2>
          <p>GTM Container: <code>{client.gtm_container_id}</code></p>
          <p><small>Auto-injected into all sites associated with this client.</small></p>
        </div>
      )}

      {/* ── Forms ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 .75rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Forms</h2>
        <button className="btn btn-primary" onClick={() => setShowNewForm(!showNewForm)}>
          {showNewForm ? 'Cancel' : '+ New Form'}
        </button>
      </div>

      {formsError && <div className="alert alert-error">{formsError}</div>}

      {showNewForm && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: '3px solid #2563eb' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>New form</h3>
          {createError && <div className="alert alert-error">{createError}</div>}
          <form onSubmit={(e) => { void handleCreateForm(e); }}>
            <div className="form-group">
              <label>Form name *</label>
              <input
                type="text"
                value={newFormName}
                onChange={(e) => setNewFormName(e.target.value)}
                required
                placeholder="e.g. Contact Form"
              />
            </div>
            <SchemaEditor schema={newFormSchema} onChange={setNewFormSchema} />
            <div style={{ display: 'flex', gap: '.75rem', marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary" disabled={creatingForm}>
                {creatingForm ? 'Creating…' : 'Create form'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowNewForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        {formsLoading ? (
          <p style={{ color: '#94a3b8' }}>Loading forms…</p>
        ) : forms.length === 0 ? (
          <p style={{ color: '#64748b' }}>No forms yet. Click "+ New Form" to create one.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Primary?</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id}>
                  <td>{f.name}</td>
                  <td>{f.status}</td>
                  <td>{f.is_primary ? 'Yes' : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '.5rem' }}>
                      <Link to={`/forms/${f.id}`} className="btn btn-secondary" style={{ fontSize: '.8rem', padding: '3px 10px' }}>
                        Edit
                      </Link>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '.8rem', padding: '3px 10px', color: '#dc2626' }}
                        onClick={() => { void handleDeleteForm(f.id, f.name); }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ color: '#64748b', fontSize: '.875rem', marginTop: '.25rem' }}>
        <em>Click a form to get its embed code and AI assistant prompt.</em>
      </p>
    </>
  );
}
